import React from "react";
import { createPortal } from "react-dom";
import { autoUpdate, flip, offset, shift, useFloating } from "@floating-ui/react-dom";
import { ChevronDown, Plus } from "lucide-react";
import usePopoverDismiss from "../hooks/usePopoverDismiss.js";
import { useToast } from "./ResultsPrimitives.jsx";
import { addableStandaloneUnits, createStandaloneUnit } from "../lib/tractorAssignmentsApi.js";
import { getReactPortalRoot } from "../utils/reactPortalRoot.js";

const TRIGGER_CLASS_NAME = "inline-flex min-h-9 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-[4px] border border-slate-300 bg-white px-2.75 py-1.5 text-[0.68rem] font-semibold text-slate-700 transition hover:border-slate-400 hover:bg-slate-50 hover:text-slate-900 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-accent/35 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50";
const SECTION_TITLE_CLASS_NAME = "m-0 px-2 pt-1.5 pb-0.5 text-[0.6rem] font-bold uppercase tracking-[0.14em] text-slate-500";

function formatDispatchDay(dispatchDate) {
  return dispatchDate ? new Date(`${dispatchDate}T12:00:00`).toLocaleDateString("fr-CA", { day: "numeric", month: "long" }) : "";
}

function AddUnitSection({ title, options, emptyLabel, busyId, onChoose }) {
  return <>
    <p className={SECTION_TITLE_CLASS_NAME}>{title}</p>
    {options.length ? options.map((option) => <button key={option.id} type="button" role="menuitem" disabled={Boolean(busyId)}
      data-add-unit-option={option.id}
      className="flex w-full items-center gap-3 px-2 py-1 text-left text-xs text-content-ink hover:bg-slate-50 disabled:cursor-wait disabled:opacity-60"
      onClick={() => onChoose(option)}>
      <span className="min-w-0 flex-1 truncate">
        <span className="font-semibold">{option.name}</span>
        {option.kind ? <span className="text-content-muted"> · {option.kind}</span> : null}
      </span>
      <span className="shrink-0 text-right tabular-nums text-content-muted">{busyId === option.id ? "Ajout…" : option.usedByLabel}</span>
    </button>) : <p className="m-0 px-2 py-1 text-xs text-content-muted">{emptyLabel}</p>}
  </>;
}

// Bouton « Ajouter » du Gantt : met une vraie remorque sans camion (« Camion à choisir ») ou un camion libre
// (« Remorque à choisir ») au Gantt de la date et de la période affichées. Jamais sur une journée fermée.
export default function GanttAddUnitButton({ catalog = null, ganttTruckIds = new Set(), dispatchDate = "", dispatchPeriod = "day", canWrite = false }) {
  const { showToast } = useToast();
  const [isOpen, setIsOpen] = React.useState(false);
  const [busyId, setBusyId] = React.useState("");
  const triggerRef = React.useRef(null);
  const { refs, floatingStyles, isPositioned } = useFloating({
    open: isOpen, strategy: "fixed", placement: "bottom-end",
    middleware: [offset(4), flip({ padding: 8 }), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
  });
  const close = React.useCallback(() => setIsOpen(false), []);
  usePopoverDismiss({ anchorElement: triggerRef.current, floatingRef: refs.floating, isOpen, onClose: close });
  const { trailers, tractors } = React.useMemo(() => addableStandaloneUnits(catalog, ganttTruckIds), [catalog, ganttTruckIds]);
  const choose = async (option, part) => {
    setBusyId(option.id);
    try {
      const result = await createStandaloneUnit({ dispatchDate, dispatchPeriod, truckId: option.id, part });
      showToast({ type: "success", title: "Ligne ajoutée au Gantt", message: result?.message || option.name });
      close();
    } catch (failure) {
      showToast({ type: "warning", title: "Ligne non ajoutée", message: failure.message });
    } finally {
      setBusyId("");
    }
  };
  const portalRoot = getReactPortalRoot();
  return <>
    <button ref={(element) => { triggerRef.current = element; refs.setReference(element); }} type="button"
      data-gantt-add-unit="true" aria-haspopup="menu" aria-expanded={isOpen ? "true" : "false"}
      disabled={!canWrite || !catalog} title={canWrite ? "Ajouter une remorque sans camion ou un camion libre au Gantt de cette journée" : "Votre rôle n'a pas ce droit."}
      className={TRIGGER_CLASS_NAME} onClick={() => setIsOpen((current) => !current)}>
      <Plus aria-hidden="true" className="h-3.5 w-3.5" strokeWidth={2.15} />
      <span>Ajouter</span>
      <ChevronDown aria-hidden="true" className="h-3.5 w-3.5 text-content-muted" strokeWidth={2.15} />
    </button>
    {isOpen && portalRoot ? createPortal(
      <div ref={refs.setFloating} role="menu" aria-label="Ajouter au Gantt" data-gantt-add-unit-menu="true"
        className="w-[22rem] max-w-[calc(100vw-1rem)] rounded-[4px] border border-line-soft bg-white py-1 shadow-[0_8px_24px_rgba(15,23,42,0.12)]"
        style={{ ...floatingStyles, visibility: isPositioned ? "visible" : "hidden", zIndex: 2200 }}>
        <AddUnitSection title="Une remorque · sans camion" options={trailers} emptyLabel="Aucune remorque sans camion."
          busyId={busyId} onChoose={(option) => void choose(option, "remorque")} />
        <AddUnitSection title="Un camion · libre" options={tractors} emptyLabel="Aucun camion libre."
          busyId={busyId} onChoose={(option) => void choose(option, "camion")} />
        <p className="m-0 mt-1 border-t border-line-soft px-2 pt-1.5 pb-0.5 text-[0.66rem] leading-4 text-content-muted">
          {formatDispatchDay(dispatchDate)} · {dispatchPeriod === "evening" ? "Soir" : "Jour"}. La ligne vide ne vaut que pour cette journée.
        </p>
      </div>,
      portalRoot,
    ) : null}
  </>;
}
